import React, { useCallback, useEffect, useMemo } from "react";
import { useSearch } from "../../../contexts/SearchContext";
import { useAppContext } from "../../../contexts/AppContext";
import { FlightBookingMockupStepCardLayout } from "./StepCardLayout";
import { useBookingFlow } from "./hooks/useBookingFlow";
import ExtraSupplementItem from "./extraSupplements/ExtraSupplementItem";
import PassengersStepCard from "./PassengersStepCard";
import "./extraSupplements/ExtrasInteractive.css";
import {
  buildFlightCardLegRowsFromDeal,
  formatExtrasSegmentDirectionLabel,
  mapPaxListWithPassengerNames,
  mapRawLdsSupplementsToMockupExtras,
} from "../adapters/flightOfferAdapter";
import { resolveFlightBaggageDisplayState } from "../adapters/flightBaggageStepPolicy";
import { computeSupplementPriceIls } from "../../../utils/lds/baggageSupplementPricing";

function segmentBlockSubtitle(legRow, segmentIndex, numSegments) {
  if (!legRow) {
    const d = formatExtrasSegmentDirectionLabel(segmentIndex, numSegments);
    return d || "טיסה";
  }
  const route = `${legRow.originCode ?? "—"} → ${legRow.destinationCode ?? "—"}`;
  const datePart = legRow.depDateDisplay || "";
  const timePart =
    legRow.departureTime && legRow.arrivalTime
      ? `${legRow.departureTime}–${legRow.arrivalTime}`
      : "";
  return [legRow.labelHe, route, datePart, timePart].filter(Boolean).join(" · ");
}

/**
 * גוף בחירת כבודה/טרולי — משותף לסוכן (BookingFlow) ולפומבי (PublicFlights).
 * אותה לוגיקת מיפוי LDS; מקור `rawDeal` חייב לכלול `supplement` כמו בחיפוש הסוכן.
 */
export function ExtrasStepCardBody({
  rawDeal: rawDealProp,
  savedDealFallback,
  extras,
  updateExtras,
  passengers,
  updatePassengers,
  ceilDisplayedSupplementUnitPrices = false,
  roomInfoOverride = null,
  hidePassengersEmbedded = false,
  headless = false,
  baggageFeeFlatUsd: baggageFeeFlatUsdProp = null,
  currencyRateUsd: currencyRateUsdProp = null,
  baggagePricingRevision = 0,
}) {
  const { roomInfo: searchRoomInfo } = useSearch();
  const roomInfo = roomInfoOverride ?? searchRoomInfo;
  const { currencyRate, getBaggageFeeFlat } = useAppContext();
  const baggageFeeFlatUsd =
    baggageFeeFlatUsdProp != null
      ? baggageFeeFlatUsdProp
      : getBaggageFeeFlat();
  const currencyRateUsd =
    currencyRateUsdProp != null
      ? Number(currencyRateUsdProp)
      : Number(currencyRate?.USD);

  const rawDeal = rawDealProp ?? savedDealFallback ?? null;
  const baggageDisplayState = useMemo(
    () => resolveFlightBaggageDisplayState(rawDeal),
    [rawDeal]
  );

  const legRows = useMemo(
    () => (rawDeal ? buildFlightCardLegRowsFromDeal(rawDeal) : []),
    [rawDeal]
  );

  const numSegments = legRows.length > 0 ? legRows.length : 1;
  const isRoundTripUi = numSegments >= 2;

  const mappedExtras = useMemo(
    () => (rawDeal ? mapRawLdsSupplementsToMockupExtras(rawDeal) : []),
    [rawDeal]
  );
  const selectableExtras = useMemo(
    () => mappedExtras.filter((row) => Number(row?.fareAmount) > 0),
    [mappedExtras]
  );

  const rowsBySegment = useMemo(() => {
    const m = new Map();
    for (const row of selectableExtras) {
      const idx = row.segmentIndex ?? 0;
      if (!m.has(idx)) m.set(idx, []);
      m.get(idx).push(row);
    }
    return m;
  }, [selectableExtras]);

  const segmentIndicesToShow = useMemo(() => {
    if (!isRoundTripUi) return [0];
    return Array.from({ length: numSegments }, (_, i) => i);
  }, [isRoundTripUi, numSegments]);

  const paxList = useMemo(
    () => mapPaxListWithPassengerNames({ roomInfo, passengers }),
    [roomInfo, passengers]
  );
  const paxIdsKey = useMemo(
    () => paxList.map((p) => p.id).join("\0"),
    [paxList]
  );

  useEffect(() => {
    updateExtras((prev) => {
      const sel = prev.supplementPaxSelections;
      if (!sel || typeof sel !== "object") return prev;
      const valid = new Set(paxList.map((p) => p.id));
      let touched = false;
      const supplementPaxSelections = { ...sel };
      for (const [k, ids] of Object.entries(supplementPaxSelections)) {
        const arr = Array.isArray(ids) ? ids : [];
        const filtered = arr.filter((id) => valid.has(id));
        if (filtered.length !== arr.length) touched = true;
        if (filtered.length === 0) delete supplementPaxSelections[k];
        else supplementPaxSelections[k] = filtered;
      }
      if (!touched) return prev;
      return { ...prev, supplementPaxSelections };
    });
  }, [paxIdsKey, paxList, updateExtras]);

  useEffect(() => {
    if (!rawDeal) return;
    updateExtras((prev) => {
      if (!selectableExtras.length) {
        return {
          ...prev,
          baggage: Array.isArray(prev.baggage) ? prev.baggage : [],
          supplementUnitFares: {},
          supplementUnitFaresIls: {},
          supplementPaxSelections: {},
        };
      }
      const validKeys = new Set(selectableExtras.map((r) => r.supplementKey));
      const supplementUnitFares = { ...prev.supplementUnitFares };
      const supplementUnitFaresIls = { ...prev.supplementUnitFaresIls };
      for (const row of selectableExtras) {
        supplementUnitFares[row.supplementKey] = row.fareAmount;
        const ils = computeSupplementPriceIls({
          fareUsd: row.fareAmount,
          currencyRateUsd,
          baggageFeeFlatUsd,
          supplement: row,
        });
        supplementUnitFaresIls[row.supplementKey] = ils;
      }
      for (const k of Object.keys(supplementUnitFares)) {
        if (!validKeys.has(k)) delete supplementUnitFares[k];
      }
      for (const k of Object.keys(supplementUnitFaresIls)) {
        if (!validKeys.has(k)) delete supplementUnitFaresIls[k];
      }
      const supplementPaxSelections = { ...prev.supplementPaxSelections };
      for (const k of Object.keys(supplementPaxSelections)) {
        if (!validKeys.has(k)) delete supplementPaxSelections[k];
      }
      return {
        ...prev,
        baggage: Array.isArray(prev.baggage) ? prev.baggage : [],
        supplementUnitFares,
        supplementUnitFaresIls,
        supplementPaxSelections,
      };
    });
  }, [rawDeal, selectableExtras, updateExtras, currencyRateUsd, baggageFeeFlatUsd, baggagePricingRevision]);

  const handleTogglePax = useCallback(
    (supplementKey, paxId, checked) => {
      updateExtras((prev) => {
        const cur = new Set(prev.supplementPaxSelections[supplementKey] || []);
        if (checked) cur.add(paxId);
        else cur.delete(paxId);
        const nextArr = Array.from(cur);
        const supplementPaxSelections = { ...prev.supplementPaxSelections };
        if (nextArr.length === 0) delete supplementPaxSelections[supplementKey];
        else supplementPaxSelections[supplementKey] = nextArr;
        return { ...prev, supplementPaxSelections };
      });
    },
    [updateExtras]
  );

  const renderSegmentList = (segmentIndex, rows) => {
    if (!rows.length) {
      return (
        <p className="fb-extras-segment__empty">
          אין אפשרויות כבודה או טרולי להוספה לכיוון זה לפי הנתונים מהספק.
        </p>
      );
    }
    return (
      <ul className="fb-extras-upsell__list">
        {rows.map((row) => {
          const eligible = paxList.filter(
            (p) => row.allowsInfant || p.role !== "infant"
          );
          const selectedIds =
            extras?.supplementPaxSelections?.[row.supplementKey] || [];
          const displayIls = Number(
            extras?.supplementUnitFaresIls?.[row.supplementKey]
          );
          const displayRow =
            Number.isFinite(displayIls) && displayIls >= 0
              ? {
                  ...row,
                  fareAmount: displayIls,
                  currency: "₪",
                  secondaryFareAmount: row.fareAmount,
                  secondaryCurrency: row.currency,
                }
              : row;
          return (
            <li key={row.supplementKey} className="fb-extras-upsell__li">
              <ExtraSupplementItem
                idBase={row.supplementKey}
                row={displayRow}
                eligiblePaxList={eligible}
                selectedIds={selectedIds}
                onTogglePax={handleTogglePax}
                ceilDisplayedUnitPrice={ceilDisplayedSupplementUnitPrices}
              />
            </li>
          );
        })}
      </ul>
    );
  };

  if (headless) return null;

  return (
    <FlightBookingMockupStepCardLayout variant="body">
      {!rawDeal ? (
        <p className="fw-ui-results-empty">
          לא נבחרה טיסה — חזרו לשלב תוצאות ובחרו הצעה.
        </p>
      ) : (
        <div className="fb-extras-upsell" dir="rtl">
          {!hidePassengersEmbedded ? (
            <section className="fw-passengers-embedded-on-details" aria-label="פרטי נוסעים">
              <PassengersStepCard
                forceActive
                compactInDetails
                passengers={passengers}
                updatePassengers={updatePassengers}
                currentStep="passengers"
              />
            </section>
          ) : null}
          <p className="fb-extras-upsell__lead">
            {baggageDisplayState.displayState === "included"
              ? "הכבודה הכלולה זוהתה אוטומטית מהספק — אין צורך להוסיף שדרוגים."
              : baggageDisplayState.displayState === "paid_upgrade"
                ? isRoundTripUi
                  ? "בחרו כבודה או טרולי לכל כיוון בנפרד — הבחירה מתעדכנת בסיכום."
                  : "הוסיפו כבודה או טרולי — הבחירה מתעדכנת מיד בסיכום ליד."
                : "אין כבודה כלולה ואין אפשרויות שדרוג זמינות לטיסה זו."}
          </p>
          {baggageDisplayState.displayState === "included" ? (
            <div className="fb-extras-segments">
              <section className="fb-extras-segment" aria-label="כבודה כלולה">
                <header className="fb-extras-segment__head">
                  <h3 className="fb-extras-segment__title">כבודה כלולה</h3>
                </header>
                <ul className="fb-extras-upsell__list">
                  {baggageDisplayState.includedLines.map((line, idx) => (
                    <li key={`included-${idx}`} className="fb-extras-upsell__li">
                      <div className="fb-extra-item fb-extra-item--included">
                        <div className="fb-extra-item__main">
                          <p className="fb-extra-item__title">{line}</p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            </div>
          ) : baggageDisplayState.displayState === "paid_upgrade" ? (
            <div className="fb-extras-segments">
              {segmentIndicesToShow.map((segIdx) => {
                const legRow = legRows[segIdx] ?? null;
                const rows = rowsBySegment.get(segIdx) ?? [];
                const title =
                  formatExtrasSegmentDirectionLabel(segIdx, numSegments) ||
                  "טיסה";
                return (
                  <section
                    key={`seg-${segIdx}`}
                    className="fb-extras-segment"
                    aria-label={title}
                  >
                    <header className="fb-extras-segment__head">
                      <h3 className="fb-extras-segment__title">{title}</h3>
                      <p className="fb-extras-segment__sub" translate="no">
                        {segmentBlockSubtitle(legRow, segIdx, numSegments)}
                      </p>
                    </header>
                    {renderSegmentList(segIdx, rows)}
                  </section>
                );
              })}
            </div>
          ) : (
            <p className="fb-extras-upsell__empty">
              אין כבודה כלולה בטיסה זו.
            </p>
          )}
        </div>
      )}
    </FlightBookingMockupStepCardLayout>
  );
}

export default function ExtrasStepCard() {
  const { savedDeal } = useSearch();
  const { selectedFlightOffer, extras, updateExtras, passengers, updatePassengers } =
    useBookingFlow();

  return (
    <ExtrasStepCardBody
      rawDeal={selectedFlightOffer?.rawDeal}
      savedDealFallback={savedDeal}
      extras={extras}
      updateExtras={updateExtras}
      passengers={passengers}
      updatePassengers={updatePassengers}
      ceilDisplayedSupplementUnitPrices
    />
  );
}
